/// <amd-module name="Dashboards/WidgetHost/HostImplementation/ModernWidgetHostImplementation" />
import { ContributionComponentProps } from "Dashboards/WidgetContracts/Internal/WidgetContributionTypes";
import { LoadedEventInfo, LoadingStatus, WidgetPresentationMode, WidgetProps, WidgetSize } from "Dashboards/WidgetContracts/WidgetProps";
import * as Resources from "Dashboards/WidgetHost/Resources";
import React = require("react");
import { Lightbox } from "Reporting/Lightbox/Lightbox";
import { format } from "VSS/Core/Util/String";
import { MakeOptional } from "VSS/Core/Util/Types";
import { VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { getLightboxSize } from "../Helpers/presentationContextHelpers";
import { WidgetHostImplementationBase, WidgetHostImplementationProps } from "./WidgetHostImplementationBase";

export interface ModernWidgetHostProps extends WidgetHostImplementationProps {
    /**
     * Props for rendering New plat widget components
     */
    widgetContributionComponentProps: ContributionComponentProps;
}

/**
 * Responsible for render details of New Platform Widgets.
 *
 * What differs:
 * -Pixel sized Lightbox Rendering
 * -Widget Rendering
 */
export class ModernWidgetHostImplementation extends WidgetHostImplementationBase<ModernWidgetHostProps> {
    protected getHostType(): string {
        return "ModernWidgetHost";
    }

    protected renderWidgetHostImplementation(widgetProps: WidgetProps): JSX.Element {
        const size = this.getRenderSize();

        return (
            <ModernWidgetHostRenderer
                key={this.props.widgetProps.id}
                widgetContributionComponentProps={this.props.widgetContributionComponentProps}
                size={size}
                shouldNotShowTitle={this.props.shouldNotShowTitle}
                {...(widgetProps as MakeOptional<WidgetProps, "size">)}
            />
        );
    }

    protected getRenderSize(): WidgetSize {
        return this.props.widgetPresentationMode === WidgetPresentationMode.lightboxView
            ? getLightboxSize()
            : this.props.convertToPixelSize(this.props.widgetProps.size);
    }

    protected normalizeEventInfo(eventInfo: LoadedEventInfo): LoadedEventInfo {
        if (eventInfo != null && eventInfo.outcome == null) {
            eventInfo = { outcome: LoadingStatus.failure, outcomeDescription: eventInfo as any };
        }

        return eventInfo;
    }

    /** Renders a modern widget in the lightbox. */
    protected onShowLightbox = (): void => {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            return (
                <Lightbox onDismiss={onDismiss} title={Resources.LightboxTitle}>
                    <div style={getLightboxSize()}>
                        <div className="widget-host render-in-lightbox" style={getLightboxSize()}>
                            <ModernWidgetHostImplementation {...this.props} widgetPresentationMode={WidgetPresentationMode.lightboxView} />
                        </div>
                    </div>
                </Lightbox>
            );
        });
    };
}

export interface ModernWidgetProps extends WidgetProps {
    /**
     * Props for rendering New plat widget components
     */
    widgetContributionComponentProps: ContributionComponentProps;
}

/** Provides an intermediate layer for preventing re-renders of children */
class ModernWidgetHostRenderer extends VssComponent<ModernWidgetProps> {
    public shouldComponentUpdate?(nextProps: Readonly<WidgetProps>, nextState: Readonly<any>, nextContext: any): boolean {
        // Behave semantically as a pure component - updates are only needed where Widget configuration props or state data has changed.
        // In particular, mode switching to config doesn't need an immediate re-render
        const needsReRender =
            this.props.name !== nextProps.name ||
            this.props.settings !== nextProps.settings ||
            !this.stringEqual(this.props.settingsVersion, nextProps.settingsVersion) ||
            !this.stringEqual(this.props.size, nextProps.size);
        return needsReRender;
    }

    public render(): JSX.Element | null {
        try {
            return (
                <WrappedComponent
                    key={this.props.id}
                    style={{ width: this.props.size.width, height: this.props.size.height }}
                    wrappedType={this.props.widgetContributionComponentProps.componentType}
                    dependencies={this.props.widgetContributionComponentProps.includes}
                    wrappedProps={this.props}
                />
            );
        } catch (error) {
            this.props.onWidgetLoaded({
                outcome: LoadingStatus.failure,
                outcomeDescription: format(Resources.WidgetError_RenderingFailed, error)
            });
            return null;
        }
    }

    private stringEqual(a: any, b: any): boolean {
        return JSON.stringify(a) === JSON.stringify(b);
    }
}
